import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { ClientPolicyService } from '../../../openapi'
import { ClientPolicyDto } from '../../../openapi/models/ClientPolicyDto'
import { commandApi } from '../../../services/birdbrain-api-client'
import { apiCall } from '../../core'
import { errorMessage } from '@/lib/utils'

// Types for our client policy data
// Currently only iOS and Android are supported by the backend
type DevicePlatform = 'ios' | 'android'

interface ClientPolicyState {
  policies: ClientPolicyDto[]
  selectedPolicy: ClientPolicyDto | null
  loading: boolean
  saving: boolean
  error: string | null
  selectedPlatform: DevicePlatform | null
}

const initialState: ClientPolicyState = {
  policies: [],
  selectedPolicy: null,
  loading: false,
  saving: false,
  error: null,
  selectedPlatform: null,
}

// Async thunks
export const fetchAllClientPolicies = createAsyncThunk(
  'clientPolicy/fetchAll',
  async (_, { rejectWithValue }) => {
    return await apiCall(
      ClientPolicyService.clientConfigControllerGetAllClientPoliciesV1,
      rejectWithValue
    )
  }
)

export const fetchClientPolicyByPlatform = createAsyncThunk(
  'clientPolicy/fetchByPlatform',
  async (platform: DevicePlatform, { rejectWithValue }) => {
    return await apiCall(
      ClientPolicyService.clientConfigControllerGetClientPolicyByPlatformV1,
      rejectWithValue,
      platform
    )
  }
)

// Saves flow through the birdbrain-api command proxy so every policy change
// (incl. the maintenance-mode outage switch) lands on the admin_actions audit
// trail (Command Log action 'client_policy.update').
export const updateClientPolicy = createAsyncThunk(
  'clientPolicy/update',
  async (policy: ClientPolicyDto, { rejectWithValue }) => {
    try {
      const result = await commandApi.updateClientPolicy(policy)
      return result.response as ClientPolicyDto
    } catch (error) {
      return rejectWithValue({ message: errorMessage(error) || 'Failed to update client policy' })
    }
  }
)

// Slice
const clientPolicySlice = createSlice({
  name: 'clientPolicy',
  initialState,
  reducers: {
    setSelectedPlatform: (state, action) => {
      state.selectedPlatform = action.payload
      // Find the policy for the selected platform
      state.selectedPolicy = state.policies.find(p => p.platform === action.payload) || null
    },
    clearSelectedPolicy: state => {
      state.selectedPolicy = null
      state.selectedPlatform = null
    },
    clearError: state => {
      state.error = null
    },
  },
  extraReducers: builder => {
    // Fetch all policies
    builder
      .addCase(fetchAllClientPolicies.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllClientPolicies.fulfilled, (state, action) => {
        state.loading = false
        state.policies = action.payload
        // If we have a selected platform, update the selected policy
        if (state.selectedPlatform) {
          state.selectedPolicy =
            action.payload.find(p => p.platform === state.selectedPlatform) || null
        }
      })
      .addCase(fetchAllClientPolicies.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to fetch client policies'
      })

    // Fetch policy by platform
    builder
      .addCase(fetchClientPolicyByPlatform.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchClientPolicyByPlatform.fulfilled, (state, action) => {
        state.loading = false
        state.selectedPolicy = action.payload
        // Update the policy in the policies array
        const index = state.policies.findIndex(p => p.platform === action.payload.platform)
        if (index !== -1) {
          state.policies[index] = action.payload
        } else {
          state.policies.push(action.payload)
        }
      })
      .addCase(fetchClientPolicyByPlatform.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to fetch client policy'
      })

    // Update policy
    builder
      .addCase(updateClientPolicy.pending, state => {
        state.saving = true
        state.error = null
      })
      .addCase(updateClientPolicy.fulfilled, (state, action) => {
        state.saving = false
        state.selectedPolicy = action.payload
        // Update the policy in the policies array
        const index = state.policies.findIndex(p => p.platform === action.payload.platform)
        if (index !== -1) {
          state.policies[index] = action.payload
        } else {
          state.policies.push(action.payload)
        }
      })
      .addCase(updateClientPolicy.rejected, (state, action) => {
        state.saving = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to update client policy'
      })
  },
})

export const { setSelectedPlatform, clearSelectedPolicy, clearError } = clientPolicySlice.actions
export const clientPolicyReducer = clientPolicySlice.reducer

// Base selector
const selectClientPolicyState = (state: { clientPolicy: ClientPolicyState }) => state.clientPolicy

// Memoized selectors
export const selectClientPolicies = createSelector(
  [selectClientPolicyState],
  clientPolicyState => clientPolicyState.policies
)

export const selectSelectedPolicy = createSelector(
  [selectClientPolicyState],
  clientPolicyState => clientPolicyState.selectedPolicy
)

export const selectSelectedPlatform = createSelector(
  [selectClientPolicyState],
  clientPolicyState => clientPolicyState.selectedPlatform
)

export const selectClientPolicyLoading = createSelector(
  [selectClientPolicyState],
  clientPolicyState => clientPolicyState.loading
)

export const selectClientPolicySaving = createSelector(
  [selectClientPolicyState],
  clientPolicyState => clientPolicyState.saving
)

export const selectClientPolicyError = createSelector(
  [selectClientPolicyState],
  clientPolicyState => clientPolicyState.error
)

export const selectPolicyByPlatform = (platform: DevicePlatform) =>
  createSelector([selectClientPolicies], policies => policies.find(p => p.platform === platform))
